import { SortDirection } from '@fullstory/components';
import { AuditMetadata } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import {
  ActivityMonitoring,
  APIEventIssueSilence,
  DefinedEventIssueSilence,
  DQMRow,
  DQMSortOrder,
  ObjectDetails,
  PageIssueSilence,
  VolumeAggregateBy,
  VolumeTimeRange,
} from '@fullstory/proto/fs/services/dx-objects';
import { int64, Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { BySlug, SpecObjectInfo } from '@fullstory/redux';
import { assertExhaustive } from '@fullstory/typeutils';
import { Point } from '@fullstory/visualizations';
import { pick } from 'lodash';
import { SnoozeUpdate } from './types';

export const JULY_TS = 1688184000 * 1000;
export const PAGES_TALLY_COLLECTION_TS = 1711103306 * 1000;
export const PROPERTIES_TALLY_COLLECTION_TS = 1722542100000; // August 2024

export const timeRangeToAggregation = {
  [VolumeTimeRange.DAY_30]: VolumeAggregateBy.DAY,
  [VolumeTimeRange.DAY_7]: VolumeAggregateBy.DAY,
  [VolumeTimeRange.HOUR_72]: VolumeAggregateBy.HOUR,
};

export const getVolumePoints = (volume: ActivityMonitoring.TimeSeriesDataPoint[]): Point[] =>
  volume
    .filter(p => p.x && p.y)
    .map(p => {
      const endDate = Timestamp.toDate(p.x);

      if (!endDate) {
        return {
          value: 0,
          endDate: new Date(),
          startDate: new Date(),
          comparisonId: '',
        };
      }

      return {
        value: int64.toNumber(p.y),
        endDate,
        startDate: endDate,
        comparisonId: '',
      };
    });

export const propertyTextAppearanceColor = (row: DQMRow) => {
  if (row.pendingProperty) {
    return 'secondary';
  }

  return undefined;
};

export const getObjectIconName = (objectType: ObjectDetails['objectType']) => {
  switch (objectType) {
    case 'DASHBOARD':
      return 'dashboard';
    case 'SEGMENT':
      return 'segments';
    case 'METRIC':
      return 'metrics';
    case 'FUNNEL':
      return 'funnels';
    case 'RETENTION':
      return 'retention';
    case 'JOURNEY':
      return 'journeys';
    case 'HEATMAP':
      return 'heatmap';
    case 'EVENT':
      return 'defined-event';
    case 'PAGE':
      return 'page';
    case 'SPEC_OBJECT_MAPPING': // TODO: add an icon for spec objects
      return null;
    default:
      return null;
  }
};

type RecordProps = {
  id: string | int64;
  name: string;
  description: string;
};

type MetadataObject =
  | Pick<AuditMetadata, 'created' | 'createdBy' | 'lastUpdated' | 'lastUpdatedBy' | 'archived'>
  | null
  | undefined;

const getMetadata = (objectDetails: ObjectDetails): MetadataObject => {
  if (objectDetails.metric) {
    return objectDetails.metric?.metric?.metadata;
  } else if (objectDetails.segment) {
    return objectDetails.segment?.segment?.metadata;
  } else if (objectDetails.funnel) {
    return objectDetails.funnel?.funnel?.metadata;
  } else if (objectDetails.retentionMetric) {
    return objectDetails.retentionMetric?.retentionMetric?.metadata;
  } else if (objectDetails.dashboard) {
    return objectDetails.dashboard?.metadata
      ? {
          created: objectDetails.dashboard.metadata.createdAt,
          createdBy: objectDetails.dashboard.metadata.createdBy,
          lastUpdated: objectDetails.dashboard.metadata.updatedAt,
          lastUpdatedBy: objectDetails.dashboard.metadata.updatedBy,
          // dashboards cannot be archived
          archived: false,
        }
      : null;
  } else if (objectDetails.journey) {
    return objectDetails.journey?.journey?.metadata;
  } else if (objectDetails.heatmap) {
    return objectDetails.heatmap?.heatmap?.metadata;
  } else if (objectDetails.definedEvent) {
    return objectDetails.definedEvent?.definedEvent?.metadata;
  } else if (objectDetails.namedElement) {
    return objectDetails.namedElement?.namedElement?.metadata;
  } else if (objectDetails.page) {
    return objectDetails.page?.page?.metadata;
  } else if (objectDetails.specObjectMapping) {
    return objectDetails.specObjectMapping?.metadata;
  }
  return null;
};

export const toObjectDetailsWithRecords = (
  objectDetails: ObjectDetails,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
): ObjectDetailsWithMetadata => {
  if (objectDetails.metric) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.metric?.metric?.metric,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.segment) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.segment?.segment?.segment,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.funnel) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.funnel?.funnel?.funnel,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.retentionMetric) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.retentionMetric?.retentionMetric?.retentionMetric,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.dashboard) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.dashboard,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.journey) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.journey?.journey?.journeyMap,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.heatmap) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.heatmap?.heatmap?.heatmap,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.definedEvent) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.definedEvent?.definedEvent?.definedEvent,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.namedElement) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: objectDetails.namedElement?.namedElement?.namedElement,
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.page) {
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: {
        id: objectDetails.page?.page?.id?.toString() || '',
        name: objectDetails.page?.page?.name || '',
        description: objectDetails.page?.page?.description || '',
      },
      metadataObj: getMetadata(objectDetails),
    });
  } else if (objectDetails.specObjectMapping) {
    const objectSlug = objectDetails?.specObjectMapping?.mapping?.objectSlug;
    return toObjectDetailsWithMetadata({
      objectDetails,
      recordObj: {
        id: objectDetails?.specObjectMapping?.mapping?.objectSlug || '',
        name: objectSlug ? specObjectsBySlug[objectSlug]?.displayName ?? '' : '',
        description: '',
      },
      metadataObj: getMetadata(objectDetails),
    });
  }
  return {} as ObjectDetailsWithMetadata;
};

type ObjectDetailsEntities = {
  dashboard?: ObjectDetails['dashboard'];
  funnel?: ObjectDetails['funnel'];
  metric?: ObjectDetails['metric'];
  retentionMetric?: ObjectDetails['retentionMetric'];
  segment?: ObjectDetails['segment'];
  heatmap?: ObjectDetails['heatmap'];
  journey?: ObjectDetails['journey'];
  event?: ObjectDetails['definedEvent'];
  element?: ObjectDetails['namedElement'];
  page?: ObjectDetails['page'];
  specObjectMapping?: ObjectDetails['specObjectMapping'];
};

export type ObjectDetailsWithMetadata = ObjectDetailsEntities & {
  id: string;
  objectType: string;
  objectViewCount: number;
  description: string;
  name: string;
  created: timestamp | null;
  createdBy: string;
  lastUpdated: timestamp | null;
  lastUpdatedBy: string;
  archived: boolean;
};

const toObjectDetailsWithMetadata = ({
  objectDetails,
  recordObj,
  metadataObj,
}: {
  objectDetails: ObjectDetails;
  recordObj: RecordProps | null | undefined;
  metadataObj: MetadataObject;
}): ObjectDetailsWithMetadata => ({
  id: objectDetails.objectId,
  name: recordObj?.name as string,
  description: recordObj?.description as string,
  objectType: objectDetails.objectType,
  objectViewCount: int64.toNumber(objectDetails.objectViewCount),
  // DX object detail entities
  ...pick(objectDetails, [
    'dashboard',
    'funnel',
    'metric',
    'retentionMetric',
    'segment',
    'heatmap',
    'journey',
    'definedEvent',
    'namedElement',
    'page',
    'specObjectMapping',
  ]),
  created: metadataObj?.created ?? null,
  createdBy: metadataObj?.createdBy as string,
  lastUpdated: metadataObj?.lastUpdated ?? null,
  lastUpdatedBy: metadataObj?.lastUpdatedBy as string,
  archived: metadataObj?.archived as boolean,
});

export const directionFromSortDirection = (direction: SortDirection) => {
  switch (direction) {
    case 'asc':
      return DQMSortOrder.Direction.DIRECTION_ASC;
    case 'desc':
      return DQMSortOrder.Direction.DIRECTION_DESC;
    default:
      return DQMSortOrder.Direction.DIRECTION_DEFAULT;
  }
};

export const PAGE_SIZE = 25;
export const PAGE_QUERY_PARAM = 'page';
export const FILTERS_QUERY_PARAM = 'filters';

export const DEFAULT_ORDER = {
  by: DQMSortOrder.By.BY_LAST_MODIFIED,
  direction: DQMSortOrder.Direction.DIRECTION_DESC,
};

export function snoozeUpdateToDefinedEventIssueSilence(
  definedEventId: string,
  silenceId: string | undefined,
  update: SnoozeUpdate,
): DefinedEventIssueSilence | undefined {
  switch (update.type) {
    case 'unsnooze':
      return undefined;
    case 'untilActivityReturns':
      return {
        definedEventId,
        id: silenceId || '',
        expiresAt: null,
      };
    case 'untilTime':
      return {
        definedEventId,
        id: silenceId || '',
        expiresAt: update.t,
      };
    default:
      assertExhaustive(update);
  }
}

export function snoozeUpdateToApiEventIssueSilence(
  apiEventId: string,
  silenceId: string | undefined,
  update: SnoozeUpdate,
): APIEventIssueSilence | undefined {
  switch (update.type) {
    case 'unsnooze':
      return undefined;
    case 'untilActivityReturns':
      return {
        apiEventId,
        id: silenceId || '',
        expiresAt: null,
      };
    case 'untilTime':
      return {
        apiEventId,
        id: silenceId || '',
        expiresAt: update.t,
      };
    default:
      assertExhaustive(update);
  }
}

export function snoozeUpdateToPageIssueSilence(
  pageId: number,
  silenceId: string | undefined,
  update: SnoozeUpdate,
): PageIssueSilence | null {
  switch (update.type) {
    case 'unsnooze':
      return null;
    case 'untilActivityReturns':
      return {
        pageId,
        id: silenceId || '',
        expiresAt: null,
      };
    case 'untilTime':
      return {
        pageId,
        id: silenceId || '',
        expiresAt: update.t,
      };
    default:
      assertExhaustive(update);
  }
}

export const sortDirectionFromDirection = (direction: DQMSortOrder.Direction) => {
  switch (direction) {
    case DQMSortOrder.Direction.DIRECTION_ASC:
      return 'asc';
    case DQMSortOrder.Direction.DIRECTION_DESC:
      return 'desc';
    default:
      return 'asc';
  }
};
